<script lang="ts">
  /*
  Implements a work around to allow two way binding of value,
  workaround mentioned in this README https://github.com/uNmAnNeR/imaskjs/tree/master/packages/svelte-imask
  */
  import {getContext, onMount, onDestroy, tick} from 'svelte';
  import Input from 'Components/Common/Form/Input.svelte';
  import {imask} from '@imask/svelte';
  import IMask from 'imask';
  import type {FactoryArg, FactoryOpts, InputMask} from 'imask';
  import Tooltip from 'Components/Engage/Tooltip.svelte';
  import HelpCircle from 'Components/Common/icons/HelpCircle.svelte';
  import type {GetContextForm} from 'Types/event';

  export let name: string = '';
  export let value: string | Number | Date = '';
  export let iMaskOptions: FactoryOpts = null;
  export let unmask: string | boolean = false;
  export let label: string = '';
  export let placeholder: string = '';
  export let required: boolean = false;
  export let disabled: boolean = false;
  export let newLayout: boolean = false;
  export let isFocused: boolean = false;
  export let autocomplete: string = 'on';
  export let containerClass: string = 'form-group';
  export let labelClass: string = '';
  export let readonly: boolean = false;
  export let tooltipText: string = '';
  export let helpText: string = '';

  let inputElement: HTMLInputElement = null;
  let iMaskRef: InputMask<FactoryArg>, attrs: any;

  $: {
    if (iMaskRef) {
      if (iMaskOptions) {
        iMaskRef.updateOptions(iMaskOptions);
      }
      writeValue(value);
      attrs.value = iMaskRef.value;
      tick().then(() => {
        // when unmounting iMaskRef may have been destroyed after a tick
        if (iMaskRef) {
          value = getValue();
        }
      });
    }
  }

  const {errors, onInput, onBlur} = getContext('form') as GetContextForm;

  const getValue = (): any => {
    if (unmask === 'typed') return iMaskRef.typedValue;
    if (unmask) return iMaskRef.unmaskedValue;
    return iMaskRef.value;
  };

  const setValue = (v: any): void => {
    v = v === null || v === undefined ? '' : v;
    if (unmask === 'typed') iMaskRef.typedValue = v;
    else if (unmask) iMaskRef.unmaskedValue = v;
    else iMaskRef.value = v;
  };

  const writeValue = (v: any): void => {
    if (
      getValue() !== v ||
      // handle cases like Number('') === 0,
      // for details see https://github.com/uNmAnNeR/imaskjs/issues/134
      (typeof v !== 'string' && value === '' && !iMaskRef.el.isActive)
    ) {
      setValue(v);
    }
  };

  onMount(() => {
    ({...attrs} = $$props);
    iMaskRef = IMask(inputElement, iMaskOptions);
    setValue(value);
  });

  onDestroy(() => {
    if (iMaskRef) iMaskRef.destroy();
    iMaskRef = undefined;
  });

  const accept = (e: any): void => {
    value = getValue();
    onInput(e);
  };
</script>

<Input
  id={name}
  {name}
  type="text"
  {containerClass}
  {label}
  {labelClass}
  {newLayout}
  {isFocused}
  {helpText}
>
  <input
    type="text"
    id={name}
    {name}
    class={`form-control ${$$props.class}`}
    class:is-invalid={$errors?.[name]?.error}
    bind:this={inputElement}
    use:imask={iMaskRef}
    {...attrs}
    on:accept={accept}
    on:accept
    on:complete
    on:input
    on:blur
    on:blur={onBlur}
    {placeholder}
    {required}
    {disabled}
    {autocomplete}
    {readonly}
    data-testid="input-{name}"
    slot="input"
  />
  <div class="tooltip-wrapper" slot="tooltip">
    {#if tooltipText}
      <Tooltip {tooltipText} position="left">
        <span class="ml-1">
          <HelpCircle />
        </span>
      </Tooltip>
    {/if}
  </div>
</Input>

<style lang="postcss">
  .tooltip-wrapper {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  }

  #tin {
    padding-right: 2.5rem;
  }
</style>
